import React, { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { useOnboarding } from '../../contexts/OnboardingContext';
import OnboardingWelcomeModal from './OnboardingWelcomeModal';
import ExampleWorkflowsModal from './ExampleWorkflowsModal';
import { EXAMPLE_WORKFLOWS } from './exampleWorkflowData';
import { CREATE_EXAMPLE_WORKFLOWS } from '../../graphql/mutations';
import { LIST_STATIONS, LIST_ROUTES } from '../../graphql/queries';
import { store } from '../../store';
import { addSnackBarMessage, setSnackBarOpen, changeSnackBarType, setSnackBarAction } from '../../store/reducers/snackBarMessages';

export default function OnboardingOrchestrator() {
    const navigate = useNavigate();
    const {
        hasSeenWelcome,
        setHasSeenWelcome,
        hasRoutes,
        routesLoading,
        showExampleWorkflowsModal,
        setShowExampleWorkflowsModal,
        setHasCustomTemplate
    } = useOnboarding();

    const [showWelcome, setShowWelcome] = useState(false);

    const [createExampleWorkflows] = useMutation(CREATE_EXAMPLE_WORKFLOWS, {
        refetchQueries: [{ query: LIST_STATIONS }, { query: LIST_ROUTES }]
    });

    useEffect(() => {
        // Show welcome modal on first visit (when hasSeenWelcome is false) and only if no routes exist
        // Wait until routes have loaded before showing to prevent flash
        if (!routesLoading && !hasSeenWelcome && !hasRoutes) {
            setShowWelcome(true);
        } else {
            setShowWelcome(false);
        }
    }, [hasSeenWelcome, hasRoutes, routesLoading]);

    const handleWelcomeNo = () => {
        setShowWelcome(false);
        setHasSeenWelcome(true);
    };

    const handleWelcomeYes = () => {
        setShowWelcome(false);
        setHasSeenWelcome(true);
        setShowExampleWorkflowsModal(true);
    };

    const handleCreateExamples = async (selectedWorkflows: string[]) => {
        const workflowsToCreate = selectedWorkflows.map((id) => EXAMPLE_WORKFLOWS[id as keyof typeof EXAMPLE_WORKFLOWS]);

        try {
            const result = await createExampleWorkflows({
                variables: {
                    input: {
                        workflows: workflowsToCreate.map((w) => ({
                            workflowId: w.id,
                            templateName: w.name,
                            stations: w.stations
                        }))
                    }
                }
            });

            setShowExampleWorkflowsModal(false);
            setHasCustomTemplate(true);

            // Navigate to the created route
            const routes = result.data?.createExampleWorkflows?.routes;
            if (routes && routes.length > 0) {
                navigate(`/stations/route/${routes[0].id}`);
            }
        } catch (err: unknown) {
            // Close the modal
            setShowExampleWorkflowsModal(false);

            // Extract error details with type assertion for Apollo error structure
            const apolloErr = err as { graphQLErrors?: Array<{ errorType?: string; message?: string }>; message?: string };
            const errorType = apolloErr?.graphQLErrors?.[0]?.errorType || '';
            let errorMessage = apolloErr?.graphQLErrors?.[0]?.message || apolloErr?.message || 'Failed to create example route';

            // Show error in snackbar
            store.dispatch(changeSnackBarType('error'));

            // For limit exceeded errors, modify message and add action link
            if (errorType.includes('LimitExceeded')) {
                // Remove "delete some existing stations" from message since it will be a link
                errorMessage = errorMessage.replace(' or delete some existing stations.', '');
                errorMessage = errorMessage.replace(' delete some existing stations.', '');

                store.dispatch(addSnackBarMessage(errorMessage + ' or'));
                store.dispatch(setSnackBarAction({ text: 'delete some existing stations.', url: '/stations/stations' }));
                store.dispatch(setSnackBarOpen(true));

                // Navigate to plans page
                navigate('/plans');
            } else {
                store.dispatch(addSnackBarMessage(errorMessage));
                store.dispatch(setSnackBarOpen(true));
            }
        }
    };

    return (
        <>
            <OnboardingWelcomeModal open={showWelcome} onClose={handleWelcomeNo} onYes={handleWelcomeYes} onNo={handleWelcomeNo} />
            <ExampleWorkflowsModal
                open={showExampleWorkflowsModal}
                onClose={() => setShowExampleWorkflowsModal(false)}
                onCreateExamples={handleCreateExamples}
            />
        </>
    );
}
